import React, { useState } from 'react';

import atribucionExportarImg from 'images/novedades/atribucion-exportar.png';
import campanaPuntosEngagementImg from 'images/novedades/campana-puntos-engagement.png';

// Diseño tomado de modal-novedades-advocatespro.html (ADV-3243, novedades de
// septiembre 2026: reporte descargable en Atribución + nuevos puntajes en
// Recompensas). Variante compacta de un panel (tarjeta de 480px) de la familia
// de "Modales Especiales" de AdvocatesPro, misma base que AdvproAmbassadorModal.
// Diferencia principal: cada feature tiene un botón "Ver cómo" que muestra la
// captura de pantalla correspondiente dentro del mismo modal (la tarjeta se
// ensancha) — las capturas son de la v3, acá no hay pantalla a la que navegar.

const PRIMARY = '#0061fe'; // $ap-primary-500
const SECONDARY = '#ee4a79'; // $ap-secondary-500
const SECONDARY_DARK = '#d9436e'; // $ap-secondary-600
const GREY_800 = '#222222';
const GREY_500 = '#3d3d3d';
const GREY_300 = '#7d7d7d';
const URBANIST = "'Urbanist', sans-serif";
const DMSANS = "'DM Sans', sans-serif";

const iconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const
};

const SparklesIcon = () => (
  <svg {...iconProps} width={14} height={14}>
    <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
    <path d="M20 3v4" />
    <path d="M22 5h-4" />
    <path d="M4 17v2" />
    <path d="M5 18H3" />
  </svg>
);

const LineChartIcon = () => (
  <svg {...iconProps} width={16} height={16}>
    <path d="M3 3v18h18" />
    <path d="m19 9-5 5-4-4-3 3" />
  </svg>
);

const TrophyIcon = () => (
  <svg {...iconProps} width={16} height={16}>
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
    <path d="M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
  </svg>
);

const EyeIcon = () => (
  <svg {...iconProps} width={13} height={13}>
    <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const MessageCircleIcon = () => (
  <svg {...iconProps} width={18} height={18}>
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg {...iconProps} width={14} height={14}>
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg {...iconProps} width={16} height={16}>
    <path d="m12 19-7-7 7-7" />
    <path d="M19 12H5" />
  </svg>
);

const CloseIcon = () => (
  <svg {...iconProps} width={18} height={18}>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

type FeatureKey = 'attribution' | 'engagement';

interface Feature {
  key: FeatureKey;
  icon: () => JSX.Element;
  title: string;
  text: string;
  /** Captura que abre el botón "Ver cómo". */
  image: string;
  imageAlt: string;
}

const FEATURES: Feature[] = [
  {
    key: 'attribution',
    icon: LineChartIcon,
    title: 'Reporte descargable en Atribución',
    text:
      'Ahora puedes descargar en Excel los datos del dashboard de atribución ' +
      '—clics, conversiones y valor generado por campaña y embajador— para ' +
      'llevarlos a tus propios análisis.',
    image: atribucionExportarImg,
    imageAlt:
      'Pantalla de Atribución con el botón Exportar en la esquina superior derecha'
  },
  {
    key: 'engagement',
    icon: TrophyIcon,
    title: 'Nuevos tipos de puntaje en Recompensas',
    text:
      'Ahora puedes definir en cada campaña cómo quieres medir e incentivar ' +
      'la participación de tus embajadores. Configura los puntos por ' +
      'publicación y por engagement, y activa recordatorios para cada campaña.',
    image: campanaPuntosEngagementImg,
    imageAlt:
      'Configuración avanzada de puntos en la creación de campaña, con "Personalizar puntos por engagement" activado'
  }
];

export interface NovedadesModalProps {
  open: boolean;
  /** Cierre con la X, "Cerrar" o clic en el backdrop: cierra sin descartar (vuelve en la próxima carga). */
  onClose: () => void;
  /** Clic en "Entendido": cierra sin descartar (vuelve en la próxima carga). */
  onUnderstood: () => void;
  /** Clic en "No volver a mostrar": única vía de descarte permanente. */
  onDontShowAgain: () => void;
  /** Clic en "Ver cómo" de una feature (solo tracking; la captura la muestra el modal). */
  onCaptureView?: (feature: FeatureKey) => void;
}

export const NovedadesModal: React.FC<NovedadesModalProps> = ({
  open,
  onClose,
  onUnderstood,
  onDontShowAgain,
  onCaptureView
}) => {
  // Feature cuya captura se está mostrando; null = vista principal. Vive acá y
  // no en Layout porque es navegación interna del modal, no visibilidad.
  const [preview, setPreview] = useState<Feature | null>(null);

  if (!open) return null;

  // El backdrop cierra sólo cuando el clic nace en el overlay mismo, no cuando
  // burbujea desde la tarjeta.
  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) onClose();
  };

  const handleCaptureOpen = (feature: Feature) => {
    setPreview(feature);
    if (onCaptureView) onCaptureView(feature.key);
  };

  return (
    <>
      <div
        className="advpro-novedades-overlay"
        onClick={handleOverlayClick}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          padding: 20,
          animation: 'advproNovedadesFadeIn 0.2s ease'
        }}
      >
        {/* Wrapper: tarjeta y botón cerrar son dos hijos flex en fila. El botón
            no está posicionado absoluto a propósito — así no se corta en
            pantallas angostas. */}
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="novedadesModalTitle"
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 14,
            // Con una captura abierta la tarjeta se ensancha para que la
            // pantalla completa (~1200px de origen) se lea sin zoom.
            maxWidth: preview ? 1008 : 528,
            width: '100%',
            animation: 'advproNovedadesScaleIn 0.25s ease'
          }}
        >
          {/* Tarjeta. overflow:hidden sólo redondea bordes; el scroll real vive
              únicamente en el body. */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              background: '#ffffff',
              borderRadius: 24,
              overflow: 'hidden',
              width: preview ? 960 : 480,
              maxWidth: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              maxHeight: 'calc(100vh - 40px)',
              fontFamily: DMSANS
            }}
          >
            {preview ? (
              // ---------- VISTA DE CAPTURA ----------
              <>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '18px 24px',
                    borderBottom: '1px solid #ececec',
                    flexShrink: 0
                  }}
                >
                  <button
                    type="button"
                    className="advpro-novedades-back-btn"
                    onClick={() => setPreview(null)}
                  >
                    <ArrowLeftIcon />
                    Volver
                  </button>
                  <h3
                    style={{
                      fontFamily: URBANIST,
                      fontSize: 15,
                      fontWeight: 600,
                      color: GREY_800,
                      margin: 0,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {preview.title}
                  </h3>
                </div>
                <div
                  style={{
                    padding: 20,
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto'
                  }}
                >
                  <img
                    src={preview.image}
                    alt={preview.imageAlt}
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      borderRadius: 12,
                      border: '1px solid #ececec'
                    }}
                  />
                </div>
              </>
            ) : (
              // ---------- VISTA PRINCIPAL ----------
              <>
                {/* HEADER — fijo, nunca scrollea. Degradado azul claro → rosa
                    claro del mockup. */}
                <div
                  style={{
                    background:
                      'linear-gradient(135deg, #e6efff 0%, #e6efff 40%, #fac7d5 100%)',
                    borderRadius: '24px 24px 0 0',
                    padding: '22px 28px 18px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                    flexShrink: 0
                  }}
                >
                  <div
                    style={{
                      display: 'inline-flex',
                      alignSelf: 'flex-start',
                      alignItems: 'center',
                      gap: 8,
                      height: 30,
                      borderRadius: 50,
                      border: `1px solid ${PRIMARY}`,
                      background: '#ffffff',
                      padding: '0 14px',
                      color: PRIMARY,
                      flexShrink: 0
                    }}
                  >
                    <SparklesIcon />
                    <span
                      style={{
                        fontFamily: URBANIST,
                        fontSize: 13,
                        fontWeight: 600,
                        color: PRIMARY,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Novedades · Septiembre 2026
                    </span>
                  </div>

                  {/* Título con degradado vertical azul → morado → rosa. El
                      texto va en un solo nodo: partirlo en spans puede perder
                      el espacio entre palabras al recortar el degradado. */}
                  <h2
                    id="novedadesModalTitle"
                    style={{
                      fontFamily: URBANIST,
                      fontSize: 25,
                      fontWeight: 700,
                      lineHeight: '29px',
                      letterSpacing: '-0.4px',
                      background:
                        'linear-gradient(180deg, #0061fe 0%, #6b3fa0 50%, #b93c5e 100%)',
                      WebkitBackgroundClip: 'text',
                      backgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      margin: 0
                    }}
                  >
                    Novedades de la nueva versión de AdvocatesPro
                  </h2>

                  <p
                    style={{
                      fontFamily: DMSANS,
                      fontSize: 14,
                      fontWeight: 400,
                      color: GREY_500,
                      lineHeight: '19px',
                      margin: 0
                    }}
                  >
                    Atribución y nuevos puntajes en Recompensas.
                  </p>
                </div>

                {/* BODY — único bloque que scrollea. flex:1 + minHeight:0 es el
                    patrón completo: sin minHeight:0 el overflow no tiene efecto. */}
                <div
                  className="advpro-novedades-body"
                  style={{
                    padding: '18px 28px 24px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 14,
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto'
                  }}
                >
                  {/* Features */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                      padding: '12px 14px',
                      background: '#fafafa',
                      borderRadius: 16
                    }}
                  >
                    {FEATURES.map(feature => {
                      const Icon = feature.icon;
                      return (
                        // flexWrap + base mínima del texto: en pantallas
                        // angostas el botón "Ver cómo" baja a su propia línea
                        // en vez de estrujar la columna de texto.
                        <div
                          key={feature.key}
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'flex-start',
                            gap: 10
                          }}
                        >
                          <div
                            style={{
                              width: 30,
                              height: 30,
                              borderRadius: '50%',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              background: '#fdedf2', // $ap-secondary-50, mismo tinte para todas
                              color: SECONDARY
                            }}
                          >
                            <Icon />
                          </div>
                          <div
                            style={{
                              flex: '1 1 12rem',
                              minWidth: 0,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 2,
                              paddingTop: 4
                            }}
                          >
                            <h4
                              style={{
                                fontFamily: URBANIST,
                                fontSize: 14,
                                fontWeight: 600,
                                color: GREY_800,
                                margin: 0
                              }}
                            >
                              {feature.title}
                            </h4>
                            <p
                              style={{
                                fontFamily: DMSANS,
                                fontSize: 13,
                                fontWeight: 400,
                                color: GREY_300,
                                lineHeight: '17px',
                                margin: 0
                              }}
                            >
                              {feature.text}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="advpro-novedades-cta"
                            onClick={() => handleCaptureOpen(feature)}
                          >
                            <EyeIcon />
                            Ver cómo
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  {/* Nota de soporte */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 14px',
                      borderRadius: 12,
                      background:
                        'linear-gradient(108.2deg, rgba(230, 239, 255, 0.7) 0%, #ffffff 50.75%, rgba(253, 237, 242, 0.7) 100%)',
                      color: PRIMARY
                    }}
                  >
                    <span style={{ display: 'flex', flexShrink: 0 }}>
                      <MessageCircleIcon />
                    </span>
                    <p
                      style={{
                        fontFamily: DMSANS,
                        fontSize: 13,
                        fontWeight: 500,
                        color: '#00358c',
                        lineHeight: 1.4,
                        margin: 0
                      }}
                    >
                      ¿Tienes dudas sobre alguna funcionalidad? Escríbenos por
                      el chat de soporte.
                    </p>
                  </div>

                  {/* Footer de acciones. Va dentro del body scrolleable (y no
                      fijo como en el modal del webinar) porque debajo lleva el
                      link "No volver a mostrar", igual que en el mockup. */}
                  <div className="advpro-novedades-footer">
                    <button
                      type="button"
                      className="advpro-novedades-btn-cancel"
                      onClick={onClose}
                    >
                      Cerrar
                    </button>
                    <button
                      type="button"
                      className="advpro-novedades-btn-primary"
                      onClick={onUnderstood}
                    >
                      <ArrowRightIcon />
                      Entendido
                    </button>
                  </div>

                  <p
                    className="advpro-novedades-dont-show"
                    onClick={onDontShowAgain}
                  >
                    No volver a mostrar
                  </p>
                </div>
              </>
            )}
          </div>

          <button
            type="button"
            className="advpro-novedades-close-btn"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <CloseIcon />
          </button>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Urbanist:wght@600;700&family=DM+Sans:wght@400;500;700&display=swap');
        @keyframes advproNovedadesFadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes advproNovedadesScaleIn { from { transform: scale(0.95); opacity: 0 } to { transform: scale(1); opacity: 1 } }
        .advpro-novedades-close-btn{width:34px;height:34px;border-radius:50%;background:#ffffff;border:none;cursor:pointer;color:${GREY_500};display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;padding:0;box-shadow:0 2px 8px rgba(0,0,0,0.1);transition:background-color 0.2s ease;}
        .advpro-novedades-close-btn:hover{background:#fafafa;}
        .advpro-novedades-close-btn:focus-visible{outline:2px solid ${PRIMARY};outline-offset:2px;}
        .advpro-novedades-back-btn{display:inline-flex;align-items:center;gap:6px;padding:6px 13px;border-radius:999px;border:none;background:#fdedf2;color:${SECONDARY};font-family:${URBANIST};font-size:13px;font-weight:700;white-space:nowrap;cursor:pointer;flex-shrink:0;transition:background-color 0.15s;}
        .advpro-novedades-back-btn:hover{background:#fac7d5;}
        .advpro-novedades-back-btn:focus-visible{outline:2px solid ${SECONDARY_DARK};outline-offset:2px;}
        .advpro-novedades-cta{flex:0 0 auto;align-self:flex-start;margin-top:4px;display:inline-flex;align-items:center;gap:5px;padding:6px 13px;border-radius:999px;border:none;background:#fdedf2;color:${SECONDARY};font-family:${URBANIST};font-size:12.5px;font-weight:700;white-space:nowrap;cursor:pointer;transition:background-color 0.15s;}
        .advpro-novedades-cta:hover{background:#fac7d5;}
        .advpro-novedades-cta:focus-visible{outline:2px solid ${SECONDARY_DARK};outline-offset:2px;}
        .advpro-novedades-footer{display:flex;align-items:center;gap:12px;}
        .advpro-novedades-btn-cancel{flex:0 0 auto;display:flex;align-items:center;justify-content:center;height:44px;padding:0 20px;border-radius:12px;border:1px solid #c3c3c3;cursor:pointer;background:#ffffff;color:${GREY_500};font-family:${URBANIST};font-size:14px;font-weight:700;white-space:nowrap;transition:background-color 0.2s ease;}
        .advpro-novedades-btn-cancel:hover{background:#fafafa;}
        .advpro-novedades-btn-cancel:focus-visible{outline:2px solid ${PRIMARY};outline-offset:2px;}
        .advpro-novedades-btn-primary{flex:1;display:flex;align-items:center;justify-content:center;gap:8px;height:44px;padding:0 16px;border-radius:12px;border:none;cursor:pointer;background:${SECONDARY};color:#ffffff;font-family:${URBANIST};font-size:14px;font-weight:700;white-space:nowrap;transition:background-color 0.2s ease;}
        .advpro-novedades-btn-primary:hover{background:${SECONDARY_DARK};}
        .advpro-novedades-btn-primary:focus-visible{outline:2px solid ${SECONDARY_DARK};outline-offset:3px;}
        .advpro-novedades-dont-show{font-family:${DMSANS};font-size:13px;font-weight:400;color:${GREY_300};text-align:center;text-decoration:underline;cursor:pointer;margin:0;}
        .advpro-novedades-dont-show:hover{color:${GREY_500};}
        @media (max-width: 480px) {
          .advpro-novedades-footer { flex-direction: column; }
          .advpro-novedades-btn-cancel, .advpro-novedades-btn-primary { flex: 1; width: 100%; }
          .advpro-novedades-cta { margin-left: 40px; }
        }
      `}</style>
    </>
  );
};

export default NovedadesModal;
